<template>
  <component
    :is="componentName"
    :href="activity.url"
    :target="isUrlSameDomain ? '_self' : '_blank'"
    @click="handleNotificationClick"
  >
    <div
      class="tw-flex tw-items-center tw-rounded-md tw-p-2 tw-transition-colors hover:tw-bg-neutral-50"
    >
      <div class="tw-flex tw-items-center">
        <div
          class="tw-mr-3 tw-h-2 tw-w-2 tw-rounded-full tw-bg-primary-500"
          :class="{ 'tw-invisible': notification.isRead }"
        />
        <component
          :is="icon.component"
          class="tw-mr-4 tw-h-4 tw-w-4"
          :class="icon.class"
        />
      </div>
      <div
        class="tw-flex tw-grow"
        :class="[
          isInDropdown
            ? 'tw-flex-col'
            : 'tw-flex-col md:tw-flex-row md:tw-items-center md:tw-justify-between md:tw-space-x-8',
        ]"
      >
        <div class="tw-text-sm" v-html="content" />
        <div class="tw-shrink-0 tw-text-xs tw-text-neutral-400">
          {{ formatRelativeDate(activity.time + 'Z') }}
        </div>
      </div>
    </div>
  </component>
</template>

<script setup>
import UserAddIcon from '@/assets/images/icons/user-add.svg'
import CheckIcon from '@/assets/images/icons/check.svg'
import CalendarIcon from '@/assets/images/icons/calendar.svg'
import ChessKingIcon from '@/assets/images/icons/chess-king.svg'
import RecommendIcon from '@/assets/images/icons/recommended.svg'
import CommentAddIcon from '@/assets/images/icons/comment-add.svg'
import NotAllowedIcon from '@/assets/images/icons/not-allowed.svg'
import PlusIcon from '@/assets/images/icons/plus.svg'
import UserResearchIcon from '@/assets/images/colored-icons/user-reasearch.svg'
import { computed } from 'vue'
import { useNotificationsStore } from '@/shared/stores/notifications.js'
import { useDateManager } from '@/shared/composables/useDateManager.js'

const props = defineProps({
  notification: {
    type: Object,
    required: true,
  },
  isInDropdown: {
    type: Boolean,
    default: false,
  },
})

const { formatRelativeDate } = useDateManager()

const activity = computed(() => {
  return props.notification.activities[0]
})
const content = computed(() => {
  const { userName, rank, reviewsCount, contentType, libraryName } =
    activity.value.data || {}
  const messages = {
    connection_request_received: `<strong>${userName}</strong> just connected with you.`,
    opportunity_accepted:
      'Your help request was <strong>approved</strong> and is now live!',
    opportunity_applied: `<strong>${userName}</strong> applied to your help request.`,
    new_rank_achieved: `You've reached <strong>${rank}</strong> rank for achieving ${reviewsCount} reviews. Congratulations!`,
    new_recommendation: `<strong>${userName}</strong> left you a recommendation.`,
    leave_review_reminder: `You haven't left review on your last session with <strong>${userName}</strong>. Write a review to help others know this mentor better.`,
    leave_recommendation_reminder: `You haven't left recommendation on your last session with <strong>${userName}</strong>. Write a recommendation to help others know this mentee better.`,
    session_rescheduled: `Your session with <strong>${userName}</strong> was rescheduled.`,
    session_rescheduled_mentee: `Your session with <strong>${userName}</strong> was rescheduled.`,
    session_declined: `Your session with <strong>${userName}</strong> was declined.`,
    session_scheduled: `Your session with <strong>${userName}</strong> was scheduled.`,
    session_completed: `Your session with <strong>${userName}</strong> was completed.`,
    session_pending_confirmation: `Your session with <strong>${userName}</strong> needs to be confirmed.`,
    session_requested: `You received new session request by <strong>${userName}</strong>.`,
    team_member_joined: `<strong>${userName}</strong> joined your team.`,
    content_library_added: `Your ${contentType} was <strong>approved</strong> and added to ${libraryName}.`,
    session_reviewed: `You received a review from <strong>${userName}</strong>.`,
    session_declined_mentee: `Your session with <strong>${userName}</strong> was declined.`,
    session_auto_declined: `Your session with <strong>${userName}</strong> was automatically declined.`,
    matching_success: `We successfully <strong>matched</strong> you with mentors!`,
    matching_failed:
      'Something went wrong while <strong>matching</strong> you with mentors. Please try again.',
  }
  if (!Object.keys(messages).includes(activity.value.action)) {
    return activity.value.action
  }
  return messages[activity.value.action]
})
const icon = computed(() => {
  const icons = {
    connection_request_received: {
      component: UserAddIcon,
      class: 'tw-text-primary-500',
    },
    opportunity_accepted: {
      component: CheckIcon,
      class: 'tw-text-success-500',
    },
    opportunity_applied: {
      component: CheckIcon,
      class: 'tw-text-success-500',
    },
    new_rank_achieved: {
      component: ChessKingIcon,
      class: 'tw-text-secondary-500',
    },
    new_recommendation: {
      component: RecommendIcon,
      class: 'tw-text-primary-500',
    },
    leave_review_reminder: {
      component: CommentAddIcon,
      class: 'tw-text-primary-500',
    },
    leave_recommendation_reminder: {
      component: CommentAddIcon,
      class: 'tw-text-primary-500',
    },
    session_rescheduled: {
      component: CalendarIcon,
      class: 'tw-text-error-500',
    },
    session_rescheduled_mentee: {
      component: CalendarIcon,
      class: 'tw-text-error-500',
    },
    session_declined: {
      component: NotAllowedIcon,
      class: 'tw-text-neutral-500',
    },
    session_scheduled: {
      component: CalendarIcon,
      class: 'tw-text-primary-500',
    },
    session_completed: {
      component: CheckIcon,
      class: 'tw-text-success-500',
    },
    session_requested: {
      component: CalendarIcon,
      class: 'tw-text-success-500',
    },
    team_member_joined: {
      component: PlusIcon,
      class: 'tw-text-secondary-500',
    },
    content_library_added: {
      component: PlusIcon,
      class: 'tw-text-success-500',
    },
    session_reviewed: {
      component: CommentAddIcon,
      class: 'tw-text-primary-500',
    },
    session_declined_mentee: {
      component: NotAllowedIcon,
      class: 'tw-text-neutral-500',
    },
    session_auto_declined: {
      component: NotAllowedIcon,
      class: 'tw-text-neutral-500',
    },
    matching_success: {
      component: UserResearchIcon,
      class: 'tw-text-success-500',
    },
    matching_failed: {
      component: NotAllowedIcon,
      class: 'tw-text-error-500',
    },
  }
  if (!Object.keys(icons).includes(activity.value.action)) {
    return {
      component: NotAllowedIcon,
      class: 'tw-text-neutral-50',
    }
  }
  return icons[activity.value.action]
})
const isUrlSameDomain = computed(() => {
  return activity.value.url.includes(window.location.origin)
})
const componentName = computed(() => {
  if (!activity.value.url) {
    return 'div'
  }
  return isUrlSameDomain.value ? 'InertiaLink' : 'a'
})

const notificationsStore = useNotificationsStore()
const handleNotificationClick = () => {
  notificationsStore.markNotificationAsRead(props.notification.id)
}
</script>
